import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import MainCard from "../../components/MainCard/MainCard";
import Spinner from "../../components/Spinner/Spinner";
import { useStore } from "../../store/StoreContext";

type Status = "loading" | "ready" | "error";

const TITLE = "New App Clients";
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;
const BACKGROUND_RETRY_DELAY_MS = 60 * 1000;

const NewAppClients = () => {
  const { lightdashStore } = useStore();
  const [status, setStatus] = useState<Status>("loading");
  // Kept only in local state on purpose: the URL contains an access token.
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const expiresAtRef = useRef<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const isFetchingRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const loadEmbedUrl = useCallback(async (background: boolean) => {
    const requestId = ++requestIdRef.current;
    const schedule = (delay: number) => {
      clearTimer();
      timerRef.current = setTimeout(() => loadEmbedUrl(true), delay);
    };

    clearTimer();
    isFetchingRef.current = true;
    if (!background) setStatus("loading");

    try {
      const { url, expiresAt } = await lightdashStore.fetchDashboardEmbed();
      if (requestId !== requestIdRef.current) return;

      const expiresAtMs = Date.parse(expiresAt);
      // An invalid response would otherwise schedule a NaN (immediate) refresh and loop.
      if (!url || Number.isNaN(expiresAtMs)) throw new Error("Invalid Lightdash embed response");
      expiresAtRef.current = expiresAtMs;
      setEmbedUrl(url);
      setStatus("ready");
      schedule(Math.max(expiresAtMs - Date.now() - REFRESH_BEFORE_EXPIRY_MS, MIN_REFRESH_DELAY_MS));
    } catch {
      if (requestId !== requestIdRef.current) return;

      // A failed background refresh keeps the current dashboard while its URL is still valid.
      if (background && expiresAtRef.current !== null && expiresAtRef.current > Date.now()) {
        schedule(Math.min(BACKGROUND_RETRY_DELAY_MS, expiresAtRef.current - Date.now()));
        return;
      }

      expiresAtRef.current = null;
      setEmbedUrl(null);
      setStatus("error");
    } finally {
      if (requestId === requestIdRef.current) isFetchingRef.current = false;
    }
  }, [lightdashStore]);

  useEffect(() => {
    const requestIds = requestIdRef;
    loadEmbedUrl(false);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible" || isFetchingRef.current) return;
      if (expiresAtRef.current !== null && expiresAtRef.current <= Date.now()) {
        loadEmbedUrl(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimer();
      // Invalidates any in-flight request so it doesn't update an unmounted component.
      requestIds.current++;
    };
  }, [loadEmbedUrl]);

  return (
    <MainCard>
      <Typography variant="h3" sx={{ alignSelf: "center", marginBottom: "10px", textAlign: "center" }}>
        {TITLE}
      </Typography>
      {status === "loading" && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "inherit" }}>
          <Spinner />
        </Box>
      )}
      {status === "error" && (
        <Box
          sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, marginTop: 4, px: 2 }}
          id="new-app-clients-error"
        >
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            We couldn't load your clients' status. Please try again in a few minutes.
          </Typography>
          <Button variant="contained" color="secondary" onClick={() => loadEmbedUrl(false)} id="new-app-clients-retry">
            Retry
          </Button>
        </Box>
      )}
      {status === "ready" && embedUrl && (
        <Box sx={{ width: "100%" }} data-clarity-mask="True">
          <iframe
            key={embedUrl}
            title={TITLE}
            src={embedUrl}
            width="100%"
            loading="lazy"
            style={{ border: "none", minHeight: "600px", height: "90vh", display: "block" }}
          />
        </Box>
      )}
    </MainCard>
  );
};

export default NewAppClients;
